import _Map, { ViewState } from "react-map-gl";
import {
  Coordinate,
  NavigationVoiceAndCoordinate,
  SpotType,
} from "~/generated/graphql.types";
import { SpotMarker } from "./marker/SpotMarker";
import { Route } from "./Route";
import GeocoderControl from "./GeocoderControl";
import { useContext, useEffect, useState } from "react";
import "react-contexify/ReactContexify.css";
import { EditModeContext } from "../..";
import { SpotForRoutingFragment } from "~/routes/_shared/fragments.generated";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

export const Map = ({
  courseId,
  style,
  viewState,
  start,
  dest,
  waypoints,
  routeAllSpots,
  routeWithoutOpt,
  navigationPoints,
  onChangeViewState,
  onClickLocation,
  focusOnSpot,
}: {
  courseId: string;
  style?: Object;
  viewState: Partial<ViewState>;
  start?: SpotForRoutingFragment;
  dest?: SpotForRoutingFragment;
  waypoints: Array<SpotForRoutingFragment>;
  routeAllSpots: Array<Coordinate> | undefined;
  routeWithoutOpt: Array<Coordinate> | undefined;
  navigationPoints: Array<NavigationVoiceAndCoordinate> | undefined;
  onChangeViewState: (viewState: ViewState) => void;
  onClickLocation: (coordinate: Coordinate) => void;
  focusOnSpot: (coordinate: Coordinate) => void;
}) => {
  const [clickedLocation, setClickLocation] = useState<Coordinate>();
  const { spotIdForEdit, setSpotIdForEdit } = useContext(EditModeContext);

  const [waypointsForRouting, setWaypointsForRouting] = useState<
    SpotForRoutingFragment[]
  >(
    waypoints.filter((waypoint) =>
      [
        SpotType.Waypoint,
        SpotType.OptionalWaypoint,
        SpotType.RoutingGuide,
        SpotType.SubaruMoment,
      ].includes(waypoint.spotType)
    )
  );
  useEffect(() => {
    setWaypointsForRouting(
      waypoints.filter((waypoint) =>
        [
          SpotType.Waypoint,
          SpotType.OptionalWaypoint,
          SpotType.RoutingGuide,
          SpotType.SubaruMoment,
        ].includes(waypoint.spotType)
      )
    );
  }, [waypoints]);
  // 上のコンポーネントにcoodinateを渡す。
  useEffect(() => {
    if (!clickedLocation) return;
    onClickLocation(clickedLocation);
  }, [clickedLocation]);

  // Spotの数などが変わるたびに、SelectedSpotsIndexをリセットする。
  useEffect(() => {
    setSpotIdForEdit(undefined);
  }, [[start, dest, ...waypoints].length]);

  return (
    <>
      <_Map
        {...viewState}
        style={style}
        mapStyle="mapbox://styles/theoroy/cl7cuuaai000214r0fs02ryug"
        mapboxAccessToken={MAPBOX_TOKEN}
        onContextMenu={(e) => {
          setClickLocation({
            lat: e.lngLat.lat,
            lon: e.lngLat.lng,
          });
        }}
        onClick={(e) => {
          setClickLocation({
            lat: e.lngLat.lat,
            lon: e.lngLat.lng,
          });
        }}
        onMove={(evt) => onChangeViewState(evt.viewState)}
      >
        {start && (
          <SpotMarker
            spot={start}
            label="S"
            draggable={false}
            onClick={() => {
              setSpotIdForEdit(start.id);
              focusOnSpot(start.coordinate);
            }}
            isSelected={spotIdForEdit === start.id}
          />
        )}
        {/* ToDo 今は無理矢理spots同じSpotTypeの要素を取ってきて長さを取得することで、orderを取ってきているが、効率がわるい。Spotの要素にしたい。*/}
        {waypoints.map((spot, i) => (
          <SpotMarker
            key={i}
            spot={spot}
            draggable={false}
            label={waypoints
              .slice(0, i + 1)
              .filter((p) => p?.spotType === spot.spotType)
              .length.toString()}
            onClick={() => {
              setSpotIdForEdit(spot.id);
              focusOnSpot(spot.coordinate);
            }}
            isSelected={spotIdForEdit === spot.id}
          />
        ))}
        {/* ToDo 今は無理矢理spots同じSpotTypeの要素を取ってきて長さを取得することで、orderを取ってきているが、効率がわるい。Spotの要素にしたい。*/}
        {dest && (
          <SpotMarker
            spot={dest}
            draggable={false}
            label="G"
            onClick={() => {
              setSpotIdForEdit(dest.id);
              focusOnSpot(dest.coordinate);
            }}
            isSelected={spotIdForEdit === dest.id}
          />
        )}

        {routeAllSpots ? (
          <Route
            id={`route-with-optional-waypoints`}
            color="#81C784"
            dotted
            route={routeAllSpots}
          />
        ) : null}
        {routeWithoutOpt ? (
          <Route
            id={`route-without-optional-waypoints`}
            color={"#FF4500"}
            route={routeWithoutOpt}
          />
        ) : null}

        <GeocoderControl
          reverseGeocode
          countries="jp"
          mapboxAccessToken={MAPBOX_TOKEN}
          position="top-right"
        />
      </_Map>
    </>
  );
};
